import { Circle, GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { DateTime } from 'luxon';
import { useSnapshot } from 'valtio';
import { StayAmenity, StayCancellationPolicy } from '../../../__generated__/graphql';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { Button } from '../../components/button';
import { ContactHostModal } from './ContactHostModal';
import { Modal } from '../../components/modal';
import RangeCalendar from '../../components/RangeCalendar';
import { Alert, Divider, LoadingBlock, Muted, Stack, Strong } from '../../components/ui';
import {
  useStayCalendarQuery,
  useStayDetailQuery,
  useStayQuoteQuery,
  useStayReviewsQuery,
} from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import {
  AMENITY_GROUPS,
  CANCELLATION_POLICY_LABELS,
  CHECK_IN_METHOD_LABELS,
  amenityLabel,
} from '../StayHostPage/stayForm';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import {
  DatesModal,
  GuestsPicker,
  HeartIcon,
  SectionTitle,
  stayCardRooms,
  useWishlistToggle,
} from './StayGuestComponents';
import { checkoutPath, formatMoney, quoteLines, stayHeadline } from './stayQuoteLines';
import { dateRangeLabel, guestSummary, type StayGuests } from './staySearchState';

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

const Page = styled.main`
  ${tw`w-full pt-6 pb-20 mx-auto`}
  max-width: 1200px;
  padding-left: 24px;
  padding-right: 24px;
  @media (min-width: 744px) {
    padding-left: 40px;
    padding-right: 40px;
  }
`;

const Title = styled.h1`
  ${tw`text-2xl font-semibold`}
  font-size: 26px;
  line-height: 30px;
  margin: 0;
  font-family: var(--font-display);
  color: var(--color-text-primary);
`;

const TitleRow = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-3 mb-6`}
`;

const Overview = styled.section`
  h2 {
    margin: 0;
    font-size: 22px;
    line-height: 26px;
    font-weight: 600;
    font-family: var(--font-display);
    color: var(--color-text-primary);
  }
  p {
    margin: 4px 0 0;
    font-size: 16px;
    color: var(--color-text-primary);
  }
`;

const RatingLine = styled.div`
  margin-top: 6px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  span + span::before {
    content: '·';
    margin: 0 6px;
  }
  u {
    text-underline-offset: 2px;
  }
`;

const HostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HostAvatar = styled.span`
  flex: 0 0 48px;
  height: 48px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-black);
  color: var(--color-on-primary, #fff);
  font-size: 18px;
  font-weight: 600;
`;

const Highlight = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  svg {
    flex: 0 0 24px;
    margin-top: 2px;
    color: var(--color-text-primary);
  }
`;

const SaveButton = styled.button`
  ${tw`flex items-center gap-2 px-3 py-2 text-sm font-medium underline cursor-pointer`}
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-primary);
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
  }
  &[aria-pressed='true'] svg {
    fill: #ff385c;
    stroke: #ff385c;
  }
  &[aria-pressed='false'] svg {
    fill: none;
  }
`;

const Photos = styled.div`
  ${tw`relative grid gap-2 overflow-hidden`}
  border-radius: var(--radius-card, 12px);
  grid-template-columns: 2fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  height: min(56vw, 460px);
  img {
    ${tw`object-cover w-full h-full`}
    background: var(--color-bg-secondary);
  }
  & > img:first-child {
    grid-row: span 2;
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    & > img:not(:first-child) {
      display: none;
    }
  }
`;

const ShowAll = styled.div`
  ${tw`absolute`}
  right: 16px;
  bottom: 16px;
`;

const Columns = styled.div`
  ${tw`grid gap-12 mt-8`}
  grid-template-columns: minmax(0, 1fr) 360px;
  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const BookingCard = styled.aside`
  ${tw`sticky p-6`}
  top: 32px;
  align-self: start;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  background: var(--color-bg-elevated, #fff);
`;

const PickerBox = styled.div`
  ${tw`grid overflow-hidden`}
  grid-template-columns: 1fr 1fr;
  border: 1px solid #b0b0b0;
  border-radius: 8px;
  margin: 24px 0 16px;
`;

const PickerCell = styled.button<{ $wide?: boolean }>`
  ${tw`flex flex-col items-start text-left cursor-pointer`}
  padding: 10px 12px;
  font-size: 14px;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  ${({ $wide }) => ($wide ? 'grid-column: span 2; border-top: 1px solid #b0b0b0;' : '')}
  & + & {
    border-left: ${({ $wide }) => ($wide ? 'none' : '1px solid #b0b0b0')};
  }
`;

const PickerLabel = styled.span`
  ${tw`text-xs font-bold uppercase`}
  font-size: 10px;
`;

const LineRow = styled.div<{ $discount?: boolean }>`
  ${tw`flex justify-between text-sm`}
  color: ${({ $discount }) => ($discount ? '#008a05' : 'var(--color-text-primary)')};
`;

const Bars = styled.div`
  ${tw`grid gap-x-10 gap-y-3`}
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
`;

const Bar = styled.div<{ $value: number }>`
  ${tw`flex items-center justify-between gap-3 text-sm`}
  &::after {
    content: '';
  }
  span:nth-child(2) {
    ${tw`flex-1`}
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(
      to right,
      var(--color-text-primary) ${({ $value }) => ($value / 5) * 100}%,
      var(--color-bg-secondary) 0
    );
  }
`;

const AmenityList = styled.ul`
  ${tw`grid gap-3 p-0 m-0 list-none sm:grid-cols-2`}
  li {
    ${tw`text-base`}
    color: var(--color-text-primary);
  }
`;

const Section = ({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
}) => (
  <section data-testid={testId}>
    <Stack $gap={4}>
      <SectionTitle>{title}</SectionTitle>
      {children}
    </Stack>
    <Divider style={{ marginTop: 32 }} />
  </section>
);

function HighlightIcon({ kind }: { kind: 'door' | 'bolt' | 'calendar' }) {
  const paths = {
    door: (
      <>
        <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M3 21h18" />
        <circle cx="14.5" cy="12" r="1" />
      </>
    ),
    bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4M9 15l2 2 4-4" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={24}
      height={24}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[kind]}
    </svg>
  );
}

function LocationMap({
  point,
}: {
  point: { latitude: number; longitude: number; radiusMeters: number };
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  if (loadError) return <Muted $size="sm">The map could not load.</Muted>;
  if (!isLoaded) return <LoadingBlock label="Loading map" />;
  const center = { lat: point.latitude, lng: point.longitude };
  return (
    <div
      style={{ height: 380, borderRadius: 12, overflow: 'hidden' }}
      data-testid="stay-location-map"
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={14}
        options={{ disableDefaultUI: true, zoomControl: true, clickableIcons: false }}
      >
        <Circle
          center={center}
          radius={point.radiusMeters}
          options={{
            fillColor: '#222222',
            fillOpacity: 0.12,
            strokeColor: '#222222',
            strokeOpacity: 0.4,
          }}
        />
      </GoogleMap>
    </div>
  );
}

const parseGuests = (params: URLSearchParams): StayGuests => {
  const n = (key: string, min: number, fallback: number) => {
    const value = Number(params.get(key));
    return Number.isInteger(value) && value >= min ? value : fallback;
  };
  return {
    adults: n('adults', 1, 1),
    children: n('children', 0, 0),
    infants: n('infants', 0, 0),
    pets: n('pets', 0, 0),
  };
};

/** A place's page (spec 016 W13). Dates and guests live in the URL. */
export default function StayDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isAuthenticated, user } = useSnapshot(authStore);
  const checkIn = params.get('checkIn') ?? undefined;
  const checkOut = params.get('checkOut') ?? undefined;
  const guests = useMemo(() => parseGuests(params), [params]);
  const [datesOpen, setDatesOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const detail = useStayDetailQuery({ variables: { id }, fetchPolicy: 'cache-and-network' });
  const calendar = useStayCalendarQuery({ variables: { id }, fetchPolicy: 'cache-and-network' });
  const reviews = useStayReviewsQuery({ variables: { stayId: id, limit: 6 } });
  const hasDates = Boolean(checkIn && checkOut);
  const quote = useStayQuoteQuery({
    variables: { id, input: { checkIn: checkIn ?? '', checkOut: checkOut ?? '', guests } },
    skip: !hasDates,
    fetchPolicy: 'cache-and-network',
  });
  const listing = detail.data?.stay;
  const stay = listing?.stayDetails;
  const cal = calendar.data?.stayCalendar;
  const blocked = useMemo(() => new Set(cal?.blockedNights ?? []), [cal]);
  const { saved, toggle } = useWishlistToggle(id, listing?.isFavorite);

  const setStay = (patch: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === 0) next.delete(key);
      else next.set(key, String(value));
    });
    setParams(next, { replace: true });
  };

  if (detail.loading && !listing) {
    return (
      <Page>
        <LoadingBlock label="Loading this place" />
      </Page>
    );
  }
  if (!listing || !stay) {
    return (
      <Page>
        <Alert tone="error" title="This place is not available">
          {detail.error ? stayErrorMessage(detail.error) : 'It may have been unlisted.'}
        </Alert>
      </Page>
    );
  }

  const photos = (listing.images ?? []).map(image => toPublicS3ObjectUrl(image.imageUrl));
  const where = [listing.location?.city, listing.location?.state].filter(Boolean).join(', ');
  const rating = Number(listing.averageRating) || 0;
  const hostName = listing.owner?.firstName || listing.owner?.displayName || 'your host';
  const hostSince = listing.owner?.createdAt
    ? DateTime.fromISO(String(listing.owner.createdAt)).year
    : null;
  const amenities = stay.amenities ?? [];
  const rules = stay.houseRules;
  const freeCancel =
    stay.cancellationPolicy === StayCancellationPolicy.Flexible ||
    stay.cancellationPolicy === StayCancellationPolicy.Moderate;
  const quoteData = hasDates ? quote.data?.stayQuote : null;
  const quoteError = hasDates && quote.error ? stayErrorMessage(quote.error) : null;
  const summary = reviews.data?.stayReviews.summary;

  const reserve = () => {
    if (!checkIn || !checkOut) {
      setDatesOpen(true);
      return;
    }
    const target = checkoutPath(id, checkIn, checkOut, guests);
    if (!isAuthenticated) {
      // W16: back to the same place and dates after signing in.
      modalActions.openSignInModal(() => navigate(target));
      return;
    }
    navigate(target);
  };

  const contactHost = () => {
    if (!isAuthenticated) {
      modalActions.openSignInModal(() => setContactOpen(true));
      return;
    }
    setContactOpen(true);
  };

  const bookingCard = (
    <BookingCard data-testid="stay-booking-card">
      <div>
        {quoteData ? (
          <>
            <Strong style={{ fontSize: 22, textDecoration: 'underline' }}>
              {formatMoney(quoteData.totalPrice)}
            </Strong>{' '}
            <span style={{ fontSize: 16 }}>
              for {quoteData.nights} night{quoteData.nights === 1 ? '' : 's'}
            </span>
          </>
        ) : (
          <>
            <Strong style={{ fontSize: 22 }}>
              {formatMoney(Number(listing.weekdayPrice) || 0)}
            </Strong>{' '}
            <Muted>night</Muted>
          </>
        )}
      </div>
      <PickerBox>
        <PickerCell
          type="button"
          onClick={() => setDatesOpen(true)}
          data-testid="stay-card-checkin"
        >
          <PickerLabel>Check-in</PickerLabel>
          <span>{checkIn ? DateTime.fromISO(checkIn).toFormat('M/d/yyyy') : 'Add date'}</span>
        </PickerCell>
        <PickerCell type="button" onClick={() => setDatesOpen(true)}>
          <PickerLabel>Checkout</PickerLabel>
          <span>{checkOut ? DateTime.fromISO(checkOut).toFormat('M/d/yyyy') : 'Add date'}</span>
        </PickerCell>
        <PickerCell
          type="button"
          $wide
          onClick={() => setGuestsOpen(true)}
          data-testid="stay-card-guests"
        >
          <PickerLabel>Guests</PickerLabel>
          <span>{guestSummary(guests)}</span>
        </PickerCell>
      </PickerBox>
      {quoteError && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="warning" title="Not available">
            {quoteError}
          </Alert>
        </div>
      )}
      <Button
        type="button"
        text={!hasDates ? 'Check availability' : stay.instantBook ? 'Reserve' : 'Request to book'}
        fullWidth
        size="lg"
        isDisabled={hasDates && (!quoteData || Boolean(quoteError))}
        isLoading={hasDates && quote.loading && !quoteData}
        onClick={reserve}
        dataTestId="stay-reserve"
      />
      {hasDates && quoteData && (
        <Stack $gap={4} style={{ marginTop: 16 }}>
          <Muted $size="sm" style={{ textAlign: 'center' }}>
            You won't be charged yet
          </Muted>
          {quoteLines(quoteData).map(line => (
            <LineRow key={line.label} $discount={line.discount}>
              <span>{line.label}</span>
              <span>
                {line.amount < 0 ? `-${formatMoney(-line.amount)}` : formatMoney(line.amount)}
              </span>
            </LineRow>
          ))}
          <Divider />
          <LineRow>
            <Strong>Total</Strong>
            <Strong data-testid="stay-quote-total">{formatMoney(quoteData.totalPrice)}</Strong>
          </LineRow>
        </Stack>
      )}
    </BookingCard>
  );

  return (
    <Page>
      <TitleRow>
        <Title>{listing.name}</Title>
        <SaveButton type="button" aria-pressed={saved} onClick={toggle} data-testid="stay-save">
          <HeartIcon />
          {saved ? 'Saved' : 'Save'}
        </SaveButton>
      </TitleRow>
      <Photos>
        {photos.slice(0, 5).map((src, index) => (
          <img key={src + index} src={src} alt={index === 0 ? (listing.name ?? 'Cover') : ''} />
        ))}
        {photos.length > 1 && (
          <ShowAll>
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Show all photos"
              onClick={() => setPhotosOpen(true)}
            />
          </ShowAll>
        )}
      </Photos>
      <Columns>
        <Stack $gap={8}>
          <Overview>
            <h2>{stayHeadline(stay.roomType, stay.propertyType, where)}</h2>
            <p>
              {stay.maxGuests} guests · {stayCardRooms(stay)} · {stay.bathrooms} bath
              {stay.bathrooms === 1 ? '' : 's'}
            </p>
            <RatingLine>
              {rating > 0 ? (
                <>
                  <span>★ {rating.toFixed(2)}</span>
                  <span>
                    <u>
                      {listing.totalRatings} review{listing.totalRatings === 1 ? '' : 's'}
                    </u>
                  </span>
                </>
              ) : (
                <span>★ New</span>
              )}
            </RatingLine>
            <Divider style={{ marginTop: 32 }} />
          </Overview>
          <section>
            <HostRow>
              <HostAvatar aria-hidden="true">{hostName.charAt(0).toUpperCase()}</HostAvatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Strong style={{ fontSize: 16 }}>Hosted by {hostName}</Strong>
                {hostSince && (
                  <div>
                    <Muted $size="sm">Hosting since {hostSince}</Muted>
                  </div>
                )}
              </div>
              {listing.owner?.id !== user?.id && (
                <Button
                  theme="outlined"
                  size="sm"
                  text="Message host"
                  buttonWidth="150px"
                  onClick={contactHost}
                  dataTestId="stay-contact-host"
                />
              )}
            </HostRow>
            <Divider style={{ marginTop: 32 }} />
          </section>
          <section data-testid="stay-highlights">
            <Stack $gap={6}>
              {stay.selfCheckIn && (
                <Highlight>
                  <HighlightIcon kind="door" />
                  <div>
                    <Strong>Self check-in</Strong>
                    <div>
                      <Muted $size="sm">
                        {stay.checkInMethod
                          ? `Check yourself in with the ${CHECK_IN_METHOD_LABELS[stay.checkInMethod].toLowerCase()}.`
                          : 'Check yourself in.'}
                      </Muted>
                    </div>
                  </div>
                </Highlight>
              )}
              {listing.stayDetails?.instantBook && (
                <Highlight>
                  <HighlightIcon kind="bolt" />
                  <div>
                    <Strong>Instant Book</Strong>
                    <div>
                      <Muted $size="sm">Book without waiting for the host to respond.</Muted>
                    </div>
                  </div>
                </Highlight>
              )}
              {freeCancel && (
                <Highlight>
                  <HighlightIcon kind="calendar" />
                  <div>
                    <Strong>Free cancellation</Strong>
                    <div>
                      <Muted $size="sm">
                        {CANCELLATION_POLICY_LABELS[stay.cancellationPolicy].description}
                      </Muted>
                    </div>
                  </div>
                </Highlight>
              )}
            </Stack>
            <Divider style={{ marginTop: 32 }} />
          </section>
          {listing.description && (
            <Section title="About this place">
              <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{listing.description}</p>
              {stay.descriptionSpace && (
                <div>
                  <Strong>The space</Strong>
                  <p style={{ whiteSpace: 'pre-line' }}>{stay.descriptionSpace}</p>
                </div>
              )}
              {stay.descriptionGuestAccess && (
                <div>
                  <Strong>Guest access</Strong>
                  <p style={{ whiteSpace: 'pre-line' }}>{stay.descriptionGuestAccess}</p>
                </div>
              )}
            </Section>
          )}
          <Section title="What this place offers" testId="stay-amenities">
            <AmenityList>
              {amenities.slice(0, 10).map(amenity => (
                <li key={amenity}>{amenityLabel(amenity)}</li>
              ))}
              {!amenities.includes(StayAmenity.SmokeAlarm) && (
                <li style={{ textDecoration: 'line-through' }}>Smoke alarm</li>
              )}
              {!amenities.includes(StayAmenity.CarbonMonoxideAlarm) && (
                <li style={{ textDecoration: 'line-through' }}>Carbon monoxide alarm</li>
              )}
            </AmenityList>
            {amenities.length > 10 && (
              <div>
                <Button
                  type="button"
                  theme="secondary"
                  text={`Show all ${amenities.length} amenities`}
                  onClick={() => setAmenitiesOpen(true)}
                />
              </div>
            )}
          </Section>
          <Section
            title={hasDates ? dateRangeLabel(checkIn, checkOut) : 'Select check-in date'}
            testId="stay-calendar"
          >
            <Muted $size="sm">
              {cal && cal.minimumNights > 1 ? `Minimum stay: ${cal.minimumNights} nights. ` : ''}
              Check-in after {stay.checkInFrom}, checkout before {stay.checkOutBy}.
            </Muted>
            {cal && (
              <div style={{ maxWidth: 420 }}>
                <RangeCalendar
                  mode="nights"
                  value={{ start: checkIn ?? null, end: checkOut ?? null }}
                  onChange={value =>
                    setStay({ checkIn: value.start ?? undefined, checkOut: value.end ?? undefined })
                  }
                  minKey={cal.from}
                  maxKey={cal.to}
                  todayKey={cal.from}
                  blocked={blocked}
                  minNights={cal.minimumNights}
                  maxNights={cal.maximumNights}
                />
              </div>
            )}
          </Section>
          <Section
            title={
              rating > 0
                ? `★ ${rating.toFixed(2)} · ${listing.totalRatings} reviews`
                : 'No reviews yet'
            }
            testId="stay-reviews"
          >
            {summary && summary.totalRatings > 0 ? (
              <>
                <Bars>
                  {(
                    [
                      ['Cleanliness', summary.cleanlinessAvg],
                      ['Accuracy', summary.accuracyAvg],
                      ['Check-in', summary.checkInAvg],
                      ['Communication', summary.communicationAvg],
                      ['Location', summary.locationAvg],
                      ['Value', summary.valueAvg],
                    ] as const
                  )
                    .filter(([, value]) => value !== null && value !== undefined)
                    .map(([label, value]) => (
                      <Bar key={label} $value={Number(value)}>
                        <span>{label}</span>
                        <span aria-hidden />
                        <span>{Number(value).toFixed(1)}</span>
                      </Bar>
                    ))}
                </Bars>
                <Stack $gap={6}>
                  {reviews.data?.stayReviews.items.map(review => (
                    <div key={review.rating.id}>
                      <Strong>{review.guestName}</Strong>
                      <div>
                        <Muted $size="sm">
                          {review.rating.stayGuestRating
                            ? `${'★'.repeat(review.rating.stayGuestRating.overall)} · `
                            : ''}
                          {DateTime.fromISO(String(review.tripDate)).toFormat('LLLL yyyy')}
                        </Muted>
                      </div>
                      {review.rating.stayGuestRating?.publicReview && (
                        <p>{review.rating.stayGuestRating.publicReview}</p>
                      )}
                    </div>
                  ))}
                </Stack>
              </>
            ) : (
              <Muted>Guests have not reviewed this place yet.</Muted>
            )}
          </Section>
          <Section title="Where you'll be" testId="stay-where">
            <Muted>{where}. The exact address is shared after you book.</Muted>
            {listing.approximateLocation && <LocationMap point={listing.approximateLocation} />}
          </Section>
          <section data-testid="stay-things-to-know">
            <SectionTitle>Things to know</SectionTitle>
            <div
              style={{
                display: 'grid',
                gap: 24,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                marginTop: 16,
              }}
            >
              <Stack $gap={4}>
                <Strong>House rules</Strong>
                <Muted $size="sm">Check-in after {stay.checkInFrom}</Muted>
                <Muted $size="sm">Checkout before {stay.checkOutBy}</Muted>
                <Muted $size="sm">{stay.maxGuests} guests maximum</Muted>
                <Muted $size="sm">
                  {rules?.petsAllowed
                    ? `Pets allowed${rules.maxPets ? ` (up to ${rules.maxPets})` : ''}`
                    : 'No pets'}
                </Muted>
                {!rules?.smokingAllowed && <Muted $size="sm">No smoking</Muted>}
                {!rules?.eventsAllowed && <Muted $size="sm">No parties or events</Muted>}
                {rules?.quietHoursFrom && rules?.quietHoursTo && (
                  <Muted $size="sm">
                    Quiet hours {rules.quietHoursFrom} to {rules.quietHoursTo}
                  </Muted>
                )}
                {rules?.additionalRules && <Muted $size="sm">{rules.additionalRules}</Muted>}
              </Stack>
              <Stack $gap={4}>
                <Strong>Safety and property</Strong>
                <Muted $size="sm">
                  {amenities.includes(StayAmenity.SmokeAlarm)
                    ? 'Smoke alarm'
                    : 'No smoke alarm reported'}
                </Muted>
                <Muted $size="sm">
                  {amenities.includes(StayAmenity.CarbonMonoxideAlarm)
                    ? 'Carbon monoxide alarm'
                    : 'No carbon monoxide alarm reported'}
                </Muted>
              </Stack>
              <Stack $gap={4}>
                <Strong>Cancellation policy</Strong>
                <Muted $size="sm">
                  {CANCELLATION_POLICY_LABELS[stay.cancellationPolicy].title}.{' '}
                  {CANCELLATION_POLICY_LABELS[stay.cancellationPolicy].description}
                </Muted>
              </Stack>
            </div>
          </section>
        </Stack>
        {bookingCard}
      </Columns>

      <ContactHostModal
        listingId={id}
        hostName={hostName}
        checkIn={checkIn}
        checkOut={checkOut}
        guests={guests}
        guestsPicked={params.has('adults')}
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
      />
      <DatesModal
        open={datesOpen}
        onClose={() => setDatesOpen(false)}
        checkIn={checkIn}
        checkOut={checkOut}
        blocked={blocked}
        minNights={cal?.minimumNights}
        maxNights={cal?.maximumNights}
        timezone={cal?.timezone}
        onApply={(start, end) => setStay({ checkIn: start, checkOut: end })}
      />
      <Modal
        isOpen={guestsOpen}
        onClose={() => setGuestsOpen(false)}
        title="Guests"
        modalWidth="420px"
      >
        <GuestsPicker
          value={guests}
          maxGuests={stay.maxGuests}
          petsAllowed={Boolean(rules?.petsAllowed)}
          onChange={next =>
            setStay({
              adults: next.adults,
              children: next.children,
              infants: next.infants,
              pets: next.pets,
            })
          }
        />
      </Modal>
      <Modal
        isOpen={photosOpen}
        onClose={() => setPhotosOpen(false)}
        title="All photos"
        modalWidth="900px"
      >
        <Stack $gap={4}>
          {photos.map((src, index) => (
            <img key={src + index} src={src} alt="" style={{ width: '100%', borderRadius: 8 }} />
          ))}
        </Stack>
      </Modal>
      <Modal
        isOpen={amenitiesOpen}
        onClose={() => setAmenitiesOpen(false)}
        title="Amenities"
        modalWidth="560px"
      >
        <Stack $gap={6}>
          {AMENITY_GROUPS.map(group => {
            const present = group.items.filter(item => amenities.includes(item));
            if (!present.length) return null;
            return (
              <div key={group.title}>
                <Strong>{group.title}</Strong>
                <AmenityList style={{ marginTop: 8 }}>
                  {present.map(item => (
                    <li key={item}>{amenityLabel(item)}</li>
                  ))}
                </AmenityList>
              </div>
            );
          })}
        </Stack>
      </Modal>
    </Page>
  );
}
